import { useCallback, useMemo, useState } from 'react';
import {
  checkedEntityIds,
  type ConsolidationState,
} from './consolidation-utils';

// The left candidate pane has exactly one highlight at a time, driven from one of
// two surfaces: the distinct-name selection in the Entity Consolidation panel, or
// a document row in a survivor's View Documents table. Modelling it as a single
// discriminated union makes "never both at once" unrepresentable rather than an
// invariant maintained by hand across several setters/effects.
export type Highlight =
  | { source: 'names'; keys: string[] }
  | { source: 'document'; entityId: number }
  | null;

// Stable empty-set identities so consumers that read these memoized values don't
// re-render on a fresh `new Set()` each time the highlight is cleared.
const EMPTY_IDS: ReadonlySet<number> = new Set();
const EMPTY_KEYS: ReadonlySet<string> = new Set();

export interface HighlightApi {
  // The entity ids whose left-pane candidate rows should light up.
  highlightedIds: ReadonlySet<number>;
  // The source entity of the highlighted document row (for the survivor's View
  // Documents table), or null when a name selection — not a document — is active.
  highlightedDocEntityId: number | null;
  // The distinct-name selection for the (controlled) Entity Consolidation panel.
  selectedNameKeys: ReadonlySet<string>;
  // Report the panel's current name selection (empty clears the highlight).
  selectNames: (keys: string[]) => void;
  // Report the document row clicked in a survivor's View Documents table (null
  // clears the highlight — e.g. clicking the same row again).
  highlightDocument: (entityId: number | null) => void;
  // Drop any highlight (used when the tearsheet re-initializes on open).
  reset: () => void;
}

// Owns the single highlight state and derives everything the panels/table read
// from it. Setting either surface's highlight inherently replaces the other, so
// the two can never be lit at once. A stale reference (a name or entity that has
// since left the merge) resolves to nothing rather than a stuck highlight.
export function useHighlight(state: ConsolidationState): HighlightApi {
  const [highlight, setHighlight] = useState<Highlight>(null);

  const selectNames = useCallback((keys: string[]) => {
    setHighlight(keys.length > 0 ? { source: 'names', keys } : null);
  }, []);

  const highlightDocument = useCallback((entityId: number | null) => {
    setHighlight(entityId !== null ? { source: 'document', entityId } : null);
  }, []);

  const reset = useCallback(() => setHighlight(null), []);

  // A document highlight only shows for an entity still checked into the merge —
  // if its row was unchecked/removed the highlight resolves away (no stuck row).
  const checkedIds = useMemo(() => new Set(checkedEntityIds(state)), [state]);

  const highlightedIds = useMemo<ReadonlySet<number>>(() => {
    if (!highlight) return EMPTY_IDS;
    if (highlight.source === 'document') {
      return checkedIds.has(highlight.entityId)
        ? new Set([highlight.entityId])
        : EMPTY_IDS;
    }
    // Every member of every selected name — a shift/ctrl multi-select lights all
    // its rows. Keys no longer in the model map to nothing.
    const keys = new Set(highlight.keys);
    const ids = new Set<number>();
    state.names.forEach((n) => {
      if (keys.has(n.key)) n.memberIds.forEach((id) => ids.add(id));
    });
    return ids;
  }, [highlight, state.names, checkedIds]);

  const highlightedDocEntityId = useMemo(() => {
    return highlight?.source === 'document' &&
      checkedIds.has(highlight.entityId)
      ? highlight.entityId
      : null;
  }, [highlight, checkedIds]);

  const selectedNameKeys = useMemo<ReadonlySet<string>>(() => {
    return highlight?.source === 'names' ? new Set(highlight.keys) : EMPTY_KEYS;
  }, [highlight]);

  return {
    highlightedIds,
    highlightedDocEntityId,
    selectedNameKeys,
    selectNames,
    highlightDocument,
    reset,
  };
}
